import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getRounds, getPlayers } from '@/lib/api';
import type { Round, Player } from '@swiss-magic/shared-types';
import { useHubOutletContext } from './TournamentHubLayoutRoute';
import { StatusPanel } from './panels/StatusPanel';
import { RegistrationPanel } from './panels/RegistrationPanel';
import { CurrentRoundPanel } from './panels/CurrentRoundPanel';
import { PublicPreviewPanel } from './panels/PublicPreviewPanel';

// Story 083 — the four overview panels. The tournament comes from
// the parent route via outlet context; rounds + players are fetched
// here because they're only needed on this page. Other hub sub-pages
// have their own data layers and don't pay the cost. Lifecycle
// actions come from the shared controller on the outlet context.

export function TournamentHubOverview() {
  const { t } = useTranslation('director');
  const { tournament, lifecycle, role, godMode, reloadTournament } = useHubOutletContext();
  const isOwner = role === 'owner';
  const [rounds, setRounds] = useState<Round[] | null>(null);
  const [players, setPlayers] = useState<Player[] | null>(null);

  useEffect(() => {
    if (!tournament) return;
    let cancelled = false;
    void Promise.all([
      getRounds(tournament.id).catch(() => [] as Round[]),
      getPlayers(tournament.id).catch(() => ({
        players: [] as Player[],
        lastUpdatedAt: '',
      })),
    ]).then(([rs, ps]) => {
      if (cancelled) return;
      setRounds(rs);
      setPlayers(ps.players);
    });
    return () => {
      cancelled = true;
    };
  }, [tournament]);

  if (!tournament) {
    return (
      <div className="text-sm text-muted-foreground">
        {t('tournament_hub.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <StatusPanel
        tournament={tournament}
        lifecycle={lifecycle}
        isOwner={isOwner}
        godMode={godMode}
      />
      <RegistrationPanel
        tournament={tournament}
        players={players}
        lifecycle={lifecycle}
        isOwner={isOwner}
        onTournamentChanged={() => void reloadTournament()}
      />
      <CurrentRoundPanel
        tournament={tournament}
        rounds={rounds}
        isOwner={isOwner}
      />
      <PublicPreviewPanel tournament={tournament} />
    </div>
  );
}
